import React, { useEffect, useState } from "react";
import Drawer from "components/Drawer";
import classNames from "classnames";
import { formatM2 } from "utils/energyPlus/buildHpxmlAuditReport";
import { formatHpxmlParseErrorForUser, getAuditErrorUserCopy } from "utils/energyPlus/auditUserMessages";
import classes from "./HpxmlAuditDrawer.module.scss";

/** Lines of R·area text (non-empty); "—" counts as 1. */
function countRAreaLines(text) {
    if (text == null || text === "" || text === "—") return 1;
    const lines = text
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean);
    return Math.max(1, lines.length);
}

function maxRAreaLines(row) {
    return Math.max(countRAreaLines(row.voltaSummary), countRAreaLines(row.hpxmlRLabel));
}

/** Rows with more than this many lines default to collapsed R columns. */
const R_AREA_COLLAPSE_THRESHOLD = 3;

function rowHasCollapsibleRDetails(row) {
    return maxRAreaLines(row) > R_AREA_COLLAPSE_THRESHOLD;
}

function isRowAutoCollapsed(row) {
    return rowHasCollapsibleRDetails(row);
}

/**
 * @param {{ expandedById: Record<string, boolean | undefined>, row: object }} props
 */
function isRDetailExpanded(expandedById, row) {
    const override = expandedById[row.id];
    if (override !== undefined) return override;
    return !isRowAutoCollapsed(row);
}

function RAreaCell({ expanded, text }) {
    if (expanded) {
        return <div className={classes.summaryCellExpanded}>{text}</div>;
    }
    const lines = (text || "—")
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean);
    if (lines.length <= 1) {
        return <div className={classes.summaryCellSingle}>{text || "—"}</div>;
    }
    const extra = lines.length - 1;
    return (
        <div className={classes.summaryCellCollapsed}>
            <span className={classes.rPreviewFirst}>{lines[0]}</span>
            <span className={classes.rPreviewMore}> (+{extra} more)</span>
        </div>
    );
}

function formatRecordList(records) {
    if (!records?.length) return "—";
    return records
        .map((rec, i) => {
            const parts = Object.entries(rec)
                .filter(([, v]) => v != null && v !== "")
                .map(([k, v]) => `${k}: ${v}`);
            return parts.length ? `${i + 1}: ${parts.join("; ")}` : null;
        })
        .filter(Boolean)
        .join("\n");
}

const SystemsBlock = ({ title, voltaLines, hpxmlRecords }) => (
    <div className={classes.systemsBlock}>
        <h4 className={classes.systemsTitle}>{title}</h4>
        <div className={classes.systemsGrid}>
            <div>
                <p className={classes.columnLabel}>Volta SNAP</p>
                <ul className={classes.lineList}>
                    {voltaLines?.length ? (
                        voltaLines.map((line) => (
                            <li key={line.key}>
                                <span className={classes.lineKey}>{line.key}</span>
                                <span className={classes.lineVal}>{line.value}</span>
                            </li>
                        ))
                    ) : (
                        <li>—</li>
                    )}
                </ul>
            </div>
            <div>
                <p className={classes.columnLabel}>HPXML</p>
                <pre className={classes.hpxmlPre}>{formatRecordList(hpxmlRecords)}</pre>
            </div>
        </div>
    </div>
);

function AuditBuildErrorPanel({ detail }) {
    if (!detail) return null;
    const copy = getAuditErrorUserCopy(detail);
    if (!copy) return null;

    const hasTechnical = copy.technicalSummary || copy.stack || copy.causeMessage || copy.causeStack;

    return (
        <div className={classes.buildErrorPanel} role="alert">
            <p className={classes.errorTitle}>{copy.title}</p>
            <p className={classes.errorLead}>
                {copy.lead} {copy.closing}
            </p>
            {hasTechnical ? (
                <details className={classes.errorDetails}>
                    <summary className={classes.errorDetailsSummary}>Technical details</summary>
                    {copy.technicalSummary ? (
                        <p className={classes.errorTechnicalLine}>{copy.technicalSummary}</p>
                    ) : null}
                    {copy.causeMessage ? (
                        <p className={classes.errorTechnicalLine}>
                            <span className={classes.errorTechnicalLabel}>Caused by: </span>
                            {copy.causeMessage}
                        </p>
                    ) : null}
                    {copy.stack ? <pre className={classes.errorPre}>{copy.stack}</pre> : null}
                    {copy.causeStack ? <pre className={classes.errorPre}>{copy.causeStack}</pre> : null}
                </details>
            ) : null}
        </div>
    );
}

const HpxmlAuditDrawer = ({ open, close, loading, error, report }) => {
    const envelopeRows = report?.envelopeRows || [];
    const systems = report?.systems;
    const enclosureError = report?.enclosureError;
    const auditBuildError = report?.auditBuildError;
    const upgradePackageError = report?.upgradePackageError;
    const showResults = !loading && !error && !report?.parseError && !auditBuildError;

    const [rDetailExpandedById, setRDetailExpandedById] = useState({});

    useEffect(() => {
        if (open) setRDetailExpandedById({});
    }, [open]);

    return (
        <Drawer
            open={open}
            close={close}
            title="Audit HPXML Translation"
            subtitle="Compare your current model to the translated HPXML. Use this as a sanity check after translation, or export the full file to see its details."
            classes={{ paperClass: classes.auditDrawerPaper }}
            bodyClass={classes.drawerBody}
        >
            {loading ? <p className={classes.muted}>Loading HPXML…</p> : null}
            {error ? <p className={classes.error}>{error}</p> : null}
            {!loading && !error && report?.parseError ? (
                <p className={classes.error}>{formatHpxmlParseErrorForUser(report.parseError)}</p>
            ) : null}
            {!loading && !error && !report?.parseError && auditBuildError ? (
                <AuditBuildErrorPanel detail={auditBuildError} />
            ) : null}

            {showResults ? (
                <>
                    {upgradePackageError ? (
                        <p className={classes.warn}>
                            The upgrade package used in the latest simulation could not be found, it may have been
                            deleted. R-value comparisons in the table may be incorrect.
                        </p>
                    ) : null}
                    {enclosureError ? (
                        <p className={classes.warn}>
                            Part of the HPXML enclosure could not be read. Area and R comparisons in the table may be
                            incomplete.
                        </p>
                    ) : null}

                    <h3 className={classes.topSectionHeading}>Envelope</h3>
                    <p className={classes.muted}>
                        Rows with many components show a [+] / [-] control to expand or collapse the Details cells.
                    </p>
                    <div className={classes.tableScroll}>
                        <table className={classes.table}>
                            <thead>
                                <tr>
                                    <th>Component</th>
                                    <th>Volta SNAP Area</th>
                                    <th>HPXML Area</th>
                                    <th>Volta SNAP Details</th>
                                    <th>HPXML Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {envelopeRows.map((row) => {
                                    const areaCellClass = (() => {
                                        if (row.id === "airTightness") return classes.areaCell;
                                        if (row.areaMatch === true)
                                            return classNames(classes.areaCell, classes.areaCellMatchOk);
                                        if (row.areaMatch === false) {
                                            return classNames(classes.areaCell, classes.areaCellMatchBad);
                                        }
                                        return classes.areaCell;
                                    })();

                                    const collapsible = rowHasCollapsibleRDetails(row);
                                    const rExpanded = isRDetailExpanded(rDetailExpandedById, row);
                                    const toggleRDetails = () => {
                                        setRDetailExpandedById((prev) => {
                                            const current = isRDetailExpanded(prev, row);
                                            return { ...prev, [row.id]: !current };
                                        });
                                    };

                                    return (
                                        <tr key={row.id}>
                                            <td className={classes.componentCell}>
                                                <div className={classes.componentCellInner}>
                                                    <span className={classes.componentLabel}>{row.label}</span>
                                                    {collapsible ? (
                                                        <button
                                                            type="button"
                                                            className={classes.rCollapseToggle}
                                                            onClick={toggleRDetails}
                                                            aria-expanded={rExpanded}
                                                            aria-label={
                                                                rExpanded
                                                                    ? `Collapse R details for ${row.label}`
                                                                    : `Expand R details for ${row.label}`
                                                            }
                                                        >
                                                            <span className={classes.rCollapseBox} aria-hidden>
                                                                {rExpanded ? "−" : "+"}
                                                            </span>
                                                        </button>
                                                    ) : null}
                                                </div>
                                            </td>
                                            <td className={areaCellClass}>
                                                {row.id === "airTightness" ? "—" : formatM2(row.voltaAreaM2)}
                                            </td>
                                            <td className={areaCellClass}>
                                                {row.id === "airTightness" ? "—" : formatM2(row.hpxmlAreaM2)}
                                            </td>
                                            <td
                                                className={classNames(classes.summaryCell, {
                                                    [classes.summaryCellCollapsible]: collapsible,
                                                })}
                                            >
                                                <RAreaCell expanded={rExpanded} text={row.voltaSummary} />
                                            </td>
                                            <td
                                                className={classNames(classes.summaryCell, {
                                                    [classes.summaryCellCollapsible]: collapsible,
                                                })}
                                            >
                                                <RAreaCell expanded={rExpanded} text={row.hpxmlRLabel} />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <ul className={classes.tableNotes}>
                        <li>
                            Attached walls are inferred from the source HOT2000 / Volta SNAP file and are required for
                            HPXML's geometry validation. They assume the same temperature on either side, and therefore
                            have a negligible impact on heat flow.
                        </li>
                        <li>
                            Gable end walls in HPXML use HOT2000 / Volta SNAP's default roof cavity assumptions for
                            ceilings with the Attic - Gable End type.
                        </li>
                    </ul>

                    <div className={classes.systemsContainer}>
                        <h3 className={classes.sectionHeading}>Systems</h3>

                        <SystemsBlock
                            title="Ventilation"
                            voltaLines={systems?.volta?.ventilation}
                            hpxmlRecords={systems?.hpxml?.ventilation}
                        />
                        <SystemsBlock
                            title="Space heating"
                            voltaLines={systems?.volta?.spaceHeating}
                            hpxmlRecords={systems?.hpxml?.spaceHeating}
                        />
                        <SystemsBlock
                            title="Secondary heating / fireplace"
                            voltaLines={systems?.volta?.secondaryHeating}
                            hpxmlRecords={systems?.hpxml?.secondaryHeating}
                        />
                        <SystemsBlock
                            title="Cooling & heat pumps"
                            voltaLines={systems?.volta?.coolingAndHeatPumps}
                            hpxmlRecords={[...(systems?.hpxml?.cooling || []), ...(systems?.hpxml?.heatPumps || [])]}
                        />
                        <SystemsBlock
                            title="Domestic hot water"
                            voltaLines={systems?.volta?.domesticHotWater}
                            hpxmlRecords={systems?.hpxml?.waterHeating}
                        />
                        <SystemsBlock
                            title="Drain water heat recovery"
                            voltaLines={systems?.volta?.drainWaterHeatRecovery}
                            hpxmlRecords={systems?.hpxml?.drainWaterHeatRecovery}
                        />
                        <SystemsBlock
                            title="On-site generation (PV)"
                            voltaLines={systems?.volta?.photovoltaics}
                            hpxmlRecords={systems?.hpxml?.photovoltaics}
                        />
                    </div>
                </>
            ) : null}
        </Drawer>
    );
};

export default HpxmlAuditDrawer;
